import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { CatalogPage } from '@/features/metric-hub/catalog/CatalogPage';
import { useTrackSkillOnDwell } from '@/features/metric-hub/onboarding/use-skill-tracker';

/**
 * Catalog route.
 *
 * View / sort / status state lives in the URL so refresh + share-a-link
 * both work. The server already filters/orders, but view + sort are pure
 * client-side preferences over the returned page of items.
 *
 * `status` defaults to `'approved'` (verified-only) so the catalog opens
 * on the source of truth instead of a mix of pending drafts and deprecated
 * entries. The chip pinned to the header lets users toggle to "show all"
 * with a single click — shareable links can opt out via `?status=all`.
 */
export const Route = createFileRoute('/_authed/metric-hub/')({
  validateSearch: z.object({
    view: z.enum(['grid', 'table', 'by-domain']).optional(),
    sort: z.enum(['recent', 'alpha', 'domain', 'trust']).optional(),
    status: z.enum(['approved', 'pending', 'deprecated', 'all']).optional(),
    /** Tab discriminator added in Fase I — 'metrics' shows the metric
     *  catalog (default for back-compat with existing links/URLs), 'sem'
     *  shows the semantic-models tab. Persisted in the URL so refresh +
     *  shared links land on the same tab. */
    kind: z.enum(['metrics', 'sem']).optional(),
  }),
  component: CatalogRoute,
});

function CatalogRoute() {
  const { view, sort, status, kind } = Route.useSearch();
  useTrackSkillOnDwell('find_metric');
  return (
    <CatalogPage
      view={view ?? 'grid'}
      sort={sort ?? 'recent'}
      status={status ?? 'approved'}
      kind={kind ?? 'metrics'}
    />
  );
}
